/* eslint-disable @typescript-eslint/naming-convention */
import Model from '../../../../../common/scripts/Toolkit/Helper/Model';
import Storage from '../../../../../common/scripts/Toolkit/Utils/Storage';
import SupportLanguage from '../../../../../common/scripts/I18n/SupportLanguage';

const STORAGE_PATH = 'GameSetting';

export enum GameSettingName {
    // audio
    MusicVolume = 'MusicVolume',
    MusicMute = 'MusicMute',
    EffectVolume = 'EffectVolume',
    EffectMute = 'EffectMute',
    BGM = 'BGM',

    // language
    PreferredLanguage = 'PreferredLanguage',

    // tutorial
    OuterLobbyTutorialShow = 'OuterLobbyTutorialShow',
    OuterLobbyTutorialComplete = 'OuterLobbyTutorialComplete',
    LobbyTutorialShow = 'LobbyTutorialShow',
    LobbyTutorialComplete = 'LobbyTutorialComplete',
    CrowdPlayTutorialShow = 'CrowdPlayTutorialShow',
    CrowdPlayTutorialComplete = 'CrowdPlayTutorialComplete',
    MultiplePlayTutorialShow = 'MultiplePlayTutorialShow',
    MultiplePlayTutorialComplete = 'MultiplePlayTutorialComplete',
    RoundPlayTutorialShow = 'RoundPlayTutorialShow',
    RoundPlayTutorialComplete = 'RoundPlayTutorialComplete',
    SinglePlayTutorialShow = 'SinglePlayTutorialShow',
    SinglePlayTutorialComplete = 'SinglePlayTutorialComplete',
}

/** indicate whether the setting should be save to local storage or not */
export type GameSettingRegistry = {
    [setting in keyof typeof GameSettingName]?: boolean;
};

/** specify the default value of each setting */
export type GameSettingConfig = {
    // audio
    [GameSettingName.MusicMute]: boolean;
    [GameSettingName.EffectMute]: boolean;
    [GameSettingName.MusicVolume]: number;
    [GameSettingName.EffectVolume]: number;
    [GameSettingName.BGM]?: number;

    // language
    [GameSettingName.PreferredLanguage]: SupportLanguage;

    // tutorial
    [GameSettingName.OuterLobbyTutorialShow]: boolean;
    [GameSettingName.OuterLobbyTutorialComplete]: boolean;
    [GameSettingName.LobbyTutorialShow]: boolean;
    [GameSettingName.LobbyTutorialComplete]: boolean;
    [GameSettingName.CrowdPlayTutorialShow]: boolean;
    [GameSettingName.CrowdPlayTutorialComplete]: boolean;
    [GameSettingName.MultiplePlayTutorialShow]: boolean;
    [GameSettingName.MultiplePlayTutorialComplete]: boolean;
    [GameSettingName.RoundPlayTutorialShow]: boolean;
    [GameSettingName.RoundPlayTutorialComplete]: boolean;
    [GameSettingName.SinglePlayTutorialShow]: boolean;
    [GameSettingName.SinglePlayTutorialComplete]: boolean;
};

export default class SettingModel extends Model implements GameSettingConfig {
    public declare [GameSettingName.MusicMute]: GameSettingConfig[GameSettingName.MusicMute];
    public declare [GameSettingName.EffectMute]: GameSettingConfig[GameSettingName.EffectMute];
    public declare [GameSettingName.MusicVolume]: GameSettingConfig[GameSettingName.MusicVolume];
    public declare [GameSettingName.EffectVolume]: GameSettingConfig[GameSettingName.EffectVolume];
    public declare [GameSettingName.BGM]: GameSettingConfig[GameSettingName.BGM];

    public declare [GameSettingName.PreferredLanguage]: GameSettingConfig[GameSettingName.PreferredLanguage];
    public declare [GameSettingName.OuterLobbyTutorialShow]: GameSettingConfig[GameSettingName.OuterLobbyTutorialShow];
    public declare [GameSettingName.OuterLobbyTutorialComplete]: GameSettingConfig[GameSettingName.OuterLobbyTutorialComplete];
    public declare [GameSettingName.LobbyTutorialShow]: GameSettingConfig[GameSettingName.LobbyTutorialShow];
    public declare [GameSettingName.LobbyTutorialComplete]: GameSettingConfig[GameSettingName.LobbyTutorialComplete];
    public declare [GameSettingName.CrowdPlayTutorialShow]: GameSettingConfig[GameSettingName.CrowdPlayTutorialShow];
    public declare [GameSettingName.CrowdPlayTutorialComplete]: GameSettingConfig[GameSettingName.CrowdPlayTutorialComplete];
    public declare [GameSettingName.MultiplePlayTutorialShow]: GameSettingConfig[GameSettingName.MultiplePlayTutorialShow];
    public declare [GameSettingName.MultiplePlayTutorialComplete]: GameSettingConfig[GameSettingName.MultiplePlayTutorialComplete];
    public declare [GameSettingName.RoundPlayTutorialShow]: GameSettingConfig[GameSettingName.RoundPlayTutorialShow];
    public declare [GameSettingName.RoundPlayTutorialComplete]: GameSettingConfig[GameSettingName.RoundPlayTutorialComplete];
    public declare [GameSettingName.SinglePlayTutorialShow]: GameSettingConfig[GameSettingName.SinglePlayTutorialShow];
    public declare [GameSettingName.SinglePlayTutorialComplete]: GameSettingConfig[GameSettingName.SinglePlayTutorialComplete];

    protected readonly _config: GameSettingConfig = null;

    constructor(
        protected readonly _registry: GameSettingRegistry,
        defaultConfig: GameSettingConfig,
    ) {
        super();
        this._config = { ...defaultConfig };
        Object.values(GameSettingName).forEach((name) => {
            Object.defineProperty(this, name, {
                get: () => this._get(name),
                set: (value) => {
                    this._set(name, value);
                },
            });
        });
    }

    public init(): void {}

    public loadLocalConfig(): void {
        const localConfig = this._load();
        if (!localConfig) {
            return;
        }
        Object.assign(this._config, localConfig);
    }

    public on<T extends keyof GameSettingConfig, TFunction extends (data: GameSettingConfig[T]) => void>(name: T, callback: TFunction): TFunction;
    public on<T extends keyof GameSettingConfig, TFunction extends (data: GameSettingConfig[T]) => void>(name: T, callback: TFunction, thisArg: any): TFunction;
    public on<T extends keyof GameSettingConfig, TFunction extends (data: GameSettingConfig[T]) => void>(
        name: T,
        callback: TFunction,
        thisArg: any,
        once: boolean,
    ): TFunction;
    public on<T extends keyof GameSettingConfig, TFunction extends (data: GameSettingConfig[T]) => void>(
        name: T,
        callback: TFunction,
        thisArg?: any,
        once?: boolean,
    ): TFunction {
        return super.on(name, callback, thisArg, once);
    }

    public once<T extends keyof GameSettingConfig, TFunction extends (data: GameSettingConfig[T]) => void>(name: T, callback: TFunction): TFunction;
    public once<T extends keyof GameSettingConfig, TFunction extends (data: GameSettingConfig[T]) => void>(
        name: T,
        callback: TFunction,
        thisArg: any,
    ): TFunction;
    public once<T extends keyof GameSettingConfig, TFunction extends (data: GameSettingConfig[T]) => void>(
        name: T,
        callback: TFunction,
        thisArg?: any,
    ): TFunction {
        return super.once(name, callback, thisArg);
    }

    public off<T extends keyof GameSettingConfig, TFunction extends (data: GameSettingConfig[T]) => void>(name: T, callback: TFunction): void;
    public off<T extends keyof GameSettingConfig, TFunction extends (data: GameSettingConfig[T]) => void>(name: T, callback: TFunction, thisArg: any): void;
    public off<T extends keyof GameSettingConfig, TFunction extends (data: GameSettingConfig[T]) => void>(name: T, callback: TFunction, thisArg?: any): void {
        super.off(name, callback, thisArg);
    }

    public emit<T extends keyof GameSettingConfig>(name: T, value: GameSettingConfig[T]): void {
        super.emit(name, value);
    }

    protected _get<T extends keyof GameSettingConfig>(name: T): GameSettingConfig[T] {
        return this._config[name];
    }

    protected _set<T extends keyof GameSettingConfig>(name: T, value: GameSettingConfig[T]): void {
        if (this._config[name] === value) {
            return;
        }
        this._config[name] = value;
        this._save();
        this.emit(name, value);
    }

    protected _load(): GameSettingConfig {
        const config: GameSettingConfig = Storage.loadFromLocal(STORAGE_PATH);
        if (config) {
            Object.entries(this._registry).forEach(([key, value]) => {
                if (!value) {
                    delete config[key];
                }
            });
        }
        return config;
    }

    protected _save() {
        const config = { ...this._config };
        Object.entries(this._registry).forEach(([key, value]) => {
            if (!value) {
                delete config[key];
            }
        });
        Storage.saveToLocal(STORAGE_PATH, config);
    }
}
